// Client-side WebCodecs export (Local Engine Phase 3) — demux-decode + segment cache.
// Frames come from an mp4box demux + VideoDecoder pipeline (frame-accurate, no seek);
// if that fails it falls back to HTMLVideoElement seeking. The timeline is encoded in
// cached segments so a re-export after a one-word change re-encodes ONLY the changed
// segment(s) and re-muxes the rest from OPFS. Video-only in v1; server export handles
// audio and remains the default + ultimate fallback.

import { Muxer as Mp4Muxer, ArrayBufferTarget as Mp4Target } from "mp4-muxer";
import { Muxer as WebmMuxer, ArrayBufferTarget as WebmTarget } from "webm-muxer";
import { makeCanvas, compositeFrame } from "./compositor";
import { createDemuxFrameSource } from "./demuxDecode";
import { SEG_SECONDS, segmentHash, loadSegment, saveSegment, chunkToPlain } from "./segmentCache";

export function canLocalExport() {
  return typeof window !== "undefined" && "VideoEncoder" in window && "VideoFrame" in window;
}

// Render ONLY the caption animation onto a fully TRANSPARENT canvas and encode an alpha
// WebM (VP9 alpha). No video, no black fill, no audio. This is the device half of the
// hybrid export: the layer is uploaded and the server either overlays it on the edited
// video+audio (burned export) or converts it to a transparent ProRes MOV.
export async function exportCaptionLayerAlpha({ layers, config, segments, width, height,
                                               fps = 30, duration, onProgress, signal }) {
  if (!canLocalExport()) throw new Error("WebCodecs not supported in this browser.");
  const t0 = performance.now();
  const base = { codec: "vp09.00.10.08", width, height, bitrate: 5_000_000, framerate: fps, alpha: "keep" };
  let cfg = null;
  for (const hw of ["prefer-hardware", "no-preference", "prefer-software"]) {
    try {
      const s = await window.VideoEncoder.isConfigSupported({ ...base, hardwareAcceleration: hw });
      if (s?.supported) { cfg = s.config || { ...base, hardwareAcceleration: hw }; break; }
    } catch { /* next */ }
  }
  if (!cfg) throw new Error("ALPHA_ENCODER_UNSUPPORTED");

  const target = new WebmTarget();
  const muxer = new WebmMuxer({ target, video: { codec: "V_VP9", width, height, frameRate: fps, alpha: true } });
  let muxHasConfig = false;
  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => { let m; if (!muxHasConfig) { m = meta; muxHasConfig = true; } muxer.addVideoChunk(chunk, m); },
    error: (e) => { throw e; },
  });
  encoder.configure(cfg);

  const canvas = makeCanvas(width, height);
  const ctx = canvas.getContext("2d", { alpha: true });
  const dur = duration || 3;
  const totalFrames = Math.max(1, Math.round(dur * fps));
  let encoded = 0;
  const { drawCaptionFrame, hasAnyCaptionObjects } = await import("@/lib/animator");
  try {
    for (let i = 0; i < totalFrames; i++) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const t = i / fps;
      ctx.clearRect(0, 0, width, height); // transparent background — no black fill
      if (hasAnyCaptionObjects(layers)) drawCaptionFrame(ctx, t, width, height, layers, config, segments, { noClear: true });
      const vf = new window.VideoFrame(canvas, { timestamp: Math.round(t * 1_000_000), duration: Math.round(1_000_000 / fps), alpha: "keep" });
      encoder.encode(vf, { keyFrame: i % Math.round(fps * 2) === 0 });
      vf.close(); encoded++;
      if (onProgress && (i % 3 === 0 || i === totalFrames - 1)) onProgress({ frame: i + 1, totalFrames, progress: Math.round(((i + 1) / totalFrames) * 100) });
    }
    await encoder.flush(); encoder.close(); muxer.finalize();
  } catch (e) {
    try { encoder.close(); } catch { /* ignore */ }
    throw e;
  }
  const blob = new Blob([target.buffer], { type: "video/webm" });
  return { blob, mime: "video/webm", width, height, fps, duration: dur, hasAlpha: true, frames: encoded, size: blob.size, totalMs: Math.round(performance.now() - t0) };
}

async function pickEncoder(width, height, framerate, prefer, codecs, bitrate = 6_000_000) {
  const all = [
    ...(codecs || ["avc1.42001f"]).map((c) => ({ container: "mp4", codec: c, muxCodec: "avc" })),
    { container: "webm", codec: "vp09.00.10.08", muxCodec: "V_VP9" },
  ];
  // If a specific container is requested, only try that one (used to produce WebM and MP4 separately).
  const options = prefer ? all.filter((o) => o.container === prefer) : all;
  for (const o of options) {
    const base = { codec: o.codec, width, height, bitrate, framerate, ...(o.container === "mp4" ? { avc: { format: "avc" } } : {}) };
    for (const hw of ["prefer-hardware", "no-preference", "prefer-software"]) {
      try {
        const s = await window.VideoEncoder.isConfigSupported({ ...base, hardwareAcceleration: hw });
        if (s?.supported) return { ...o, config: s.config || { ...base, hardwareAcceleration: hw }, hw };
      } catch { /* next */ }
    }
  }
  return null;
}

// ---- frame sources (unified: getDrawable(t) -> drawable, advance()/close()) ----
function seekingSource(url) {
  let v; let ready;
  const load = () => new Promise((resolve, reject) => {
    v = document.createElement("video"); v.crossOrigin = "anonymous"; v.muted = true; v.preload = "auto"; v.src = url;
    v.onloadedmetadata = () => resolve(); v.onerror = () => reject(new Error("video load failed"));
  });
  return {
    async init() { ready = load(); await ready; },
    async getDrawable(t) {
      await new Promise((resolve) => {
        const done = () => { v.removeEventListener("seeked", done); resolve(); };
        v.addEventListener("seeked", done);
        v.currentTime = Math.min(t, (v.duration || t) - 0.001);
      });
      return v;
    },
    async skip() { /* no-op for seeking */ },
    close() { try { v.src = ""; } catch { /* ignore */ } },
  };
}

function demuxSource(dsrc) {
  let cur = null; let curT = -1; let done = false;
  async function advanceTo(t) {
    // pull frames until the current frame's time covers t (keep the latest that does)
    while (!done && (cur === null || curT < t - 1e-4)) {
      const f = await dsrc.next();
      if (!f) { done = true; break; }
      const ft = (f.timestamp || 0) / 1_000_000;
      if (cur && ft > t + 1e-4) { // overshot: keep cur, but we already consumed f — use f as new cur
        if (cur) cur.close();
        cur = f; curT = ft; return;
      }
      if (cur) cur.close();
      cur = f; curT = ft;
    }
  }
  return {
    async init() { /* dsrc already ready */ },
    async getDrawable(t) { await advanceTo(t); return cur; },
    async skip(t) { await advanceTo(t); },
    close() { if (cur) { try { cur.close(); } catch { /* ignore */ } } dsrc.close(); },
  };
}

// AAC-encode a rendered AudioBuffer straight into the MP4 muxer.
async function encodeAudio(buf, muxer, signal) {
  const ch = buf.numberOfChannels, sr = buf.sampleRate;
  const cfg = { codec: "mp4a.40.2", sampleRate: sr, numberOfChannels: ch, bitrate: 192_000 };
  const sup = await window.AudioEncoder.isConfigSupported(cfg);
  if (!sup?.supported) throw new Error("AAC_UNSUPPORTED");
  let err = null;
  const ae = new window.AudioEncoder({ output: (chunk, meta) => muxer.addAudioChunk(chunk, meta), error: (e) => { err = e; } });
  ae.configure(cfg);
  const block = sr; // 1s of audio per AudioData
  const chans = Array.from({ length: ch }, (_, i) => buf.getChannelData(i));
  for (let off = 0; off < buf.length; off += block) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    if (err) throw err;
    const n = Math.min(block, buf.length - off);
    const data = new Float32Array(n * ch);
    for (let c = 0; c < ch; c++) data.set(chans[c].subarray(off, off + n), c * n);
    const ad = new window.AudioData({ format: "f32-planar", sampleRate: sr, numberOfFrames: n, numberOfChannels: ch, timestamp: Math.round((off / sr) * 1_000_000), data });
    ae.encode(ad); ad.close();
  }
  await ae.flush(); ae.close();
  if (err) throw err;
}

export async function exportLocal({ videoUrl, layers, config, segments, width, height,
                                    fps = 30, duration, onProgress, signal, useCache = true, container,
                                    codecs, bitrate, audioBuffer, audioPromise, requireSource = false }) {
  if (!canLocalExport()) throw new Error("WebCodecs not supported in this browser.");
  const t0 = performance.now();
  const enc = await pickEncoder(width, height, fps, container, codecs, bitrate);
  if (!enc) throw new Error(container === "mp4" ? "MP4_UNSUPPORTED" : "No supported WebCodecs encoder on this device.");

  // frame source: prefer demux-decode, fall back to seeking
  let source = null;
  let sourceKind = "none";
  if (videoUrl) {
    try {
      const dsrc = await createDemuxFrameSource(videoUrl, onProgress && requireSource ? (f) => onProgress({ loading: f, progress: 0 }) : undefined);
      source = demuxSource(dsrc); sourceKind = "demux";
    } catch (e) {
      try { source = seekingSource(videoUrl); await source.init(); sourceKind = "seek"; }
      catch { source = null; sourceKind = "none"; }
    }
    if (source && sourceKind === "demux") await source.init();
  }
  // Full exports must never silently drop the picture (captions over black) — fail so the caller falls back.
  if (requireSource && videoUrl && sourceKind !== "demux") throw new Error("SOURCE_DECODE_FAILED");

  const dur = duration || 3;
  const totalFrames = Math.max(1, Math.round(dur * fps));
  const canvas = makeCanvas(width, height);
  const ctx = canvas.getContext("2d");

  let target, muxer;
  const withAudio = !!(audioBuffer || audioPromise) && enc.container === "mp4";
  if (enc.container === "mp4") {
    target = new Mp4Target();
    muxer = new Mp4Muxer({ target, fastStart: "in-memory", firstTimestampBehavior: "offset", video: { codec: enc.muxCodec, width, height },
      ...(withAudio ? { audio: { codec: "aac", numberOfChannels: 2, sampleRate: 48000 } } : {}) });
  }
  else { target = new WebmTarget(); muxer = new WebmMuxer({ target, video: { codec: enc.muxCodec, width, height, frameRate: fps } }); }

  // Encoder: collects this-segment chunks in `collect` + captures decoderConfig.
  let collect = null; let liveDescription = null;
  const encoder = new window.VideoEncoder({
    output: (chunk, meta) => {
      if (meta?.decoderConfig?.description && !liveDescription) liveDescription = new Uint8Array(meta.decoderConfig.description);
      if (collect) collect.push(chunkToPlain(chunk));
    },
    error: (e) => { throw e; },
  });
  encoder.configure(enc.config);

  let muxHasConfig = false;
  const addToMux = (chunk, description) => {
    let meta;
    if (!muxHasConfig) { meta = { decoderConfig: { codec: enc.config.codec, description: description ? description.buffer || description : undefined } }; muxHasConfig = true; }
    muxer.addVideoChunk(chunk, meta);
  };

  const sourceKey = `${videoUrl ? `url:${videoUrl}` : "solid"}|br:${enc.config.bitrate || ""}`;
  const segFrames = Math.max(1, Math.round(SEG_SECONDS * fps));
  let segsTotal = 0, segsReused = 0, encoded = 0;

  try {
    for (let f0 = 0; f0 < totalFrames; f0 += segFrames) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      const f1 = Math.min(totalFrames, f0 + segFrames);
      const st = f0 / fps, et = f1 / fps;
      segsTotal++;
      const key = useCache ? await segmentHash({ sourceKey, t0: st, t1: et, fps, width, height, codec: enc.config.codec, layers, config, segments }) : null;
      const cached = key ? await loadSegment(key) : null;

      if (cached && cached.chunks.length) {
        // Re-mux from cache; advance the (streaming) source so decode stays aligned.
        const desc = cached.description || liveDescription;
        for (const c of cached.chunks) addToMux(c, desc);
        if (source && source.skip) await source.skip(et);
        segsReused++;
      } else {
        collect = [];
        for (let i = f0; i < f1; i++) {
          const t = i / fps;
          const drawable = source ? await source.getDrawable(t) : null;
          if (requireSource && videoUrl && i === 0 && !drawable) throw new Error("SOURCE_DECODE_FAILED");
          compositeFrame(ctx, drawable, t, width, height, { layers, config, segments });
          const vf = new window.VideoFrame(canvas, { timestamp: Math.round(t * 1_000_000), duration: Math.round(1_000_000 / fps) });
          encoder.encode(vf, { keyFrame: i === f0 }); // keyframe at each segment start
          vf.close(); encoded++;
        }
        await encoder.flush(); // flush so this segment's chunks are complete
        const desc = liveDescription;
        const plainChunks = collect;
        collect = null;
        // add to muxer + save to cache
        for (const pc of plainChunks) addToMux(new window.EncodedVideoChunk({ type: pc.type, timestamp: pc.timestamp, duration: pc.duration, data: pc.data }), desc);
        if (key) await saveSegment(key, { codec: enc.config.codec, description: desc, chunks: plainChunks });
      }
      if (onProgress) onProgress({ frame: f1, totalFrames, progress: Math.round((f1 / totalFrames) * 100), segsTotal, segsReused });
    }
    encoder.close();
    if (withAudio) await encodeAudio(audioBuffer || await audioPromise, muxer, signal);
    muxer.finalize();
  } catch (e) {
    try { encoder.close(); } catch { /* ignore */ }
    if (source) source.close();
    throw e;
  }
  if (source) source.close();

  const mime = enc.container === "mp4" ? "video/mp4" : "video/webm";
  const totalMs = Math.round(performance.now() - t0);
  return {
    blob: new Blob([target.buffer], { type: mime }),
    ext: enc.container === "mp4" ? "mp4" : "webm",
    mime,
    codec: enc.container === "mp4" ? "H.264" : "VP9",
    hardware: enc.hw === "prefer-hardware",
    frames: encoded,
    segsTotal, segsReused,
    frameSource: sourceKind,
    encodeFps: encoded ? Math.round((encoded / totalMs) * 1000) : 0,
    totalMs,
  };
}
